<template>
  <div class="container-fluid">
    <Breadcrumbs title="Carga múltiple con Excel" main="Cargar Productos GS1" />
    <div class="row">
      <div class="col-12">
        <div class="card carga-card">
          <div class="card-body">
            <div class="row g-4">
              <div class="col-md-7">
                <label class="carga-label" for="excelGS1">Cargar Excel:</label>
                <input id="excelGS1" ref="excelFileInput" type="file" accept=".xlsx,.xls"
                  class="form-control carga-file" :class="{ 'is-invalid': excelFileError }" :disabled="loadingExcel"
                  @change="onExcelFileChange" />
                <p class="carga-nota">¡Subir Un Maximo De {{ maxFilasExcel }} Productos!</p>

                <p v-if="excelFileError" class="text-danger small mt-2 mb-0">{{ excelFileError }}</p>

                <div v-if="loadingExcel && excelProgressTotal" class="carga-progreso">
                  <div class="d-flex justify-content-between small text-muted mb-1">
                    <span>Procesando códigos en GS1...</span>
                    <span>{{ excelProgressDone }}/{{ excelProgressTotal }}</span>
                  </div>
                  <div class="progress">
                    <div class="progress-bar" :style="{ width: porcentajeProgreso + '%' }"></div>
                  </div>
                </div>

                <button v-if="excelFile" type="button" class="btn btn-pill btn-procesar mt-3" :disabled="loadingExcel"
                  @click="procesarExcel">
                  {{ loadingExcel ? "Procesando" : "Procesar" }}
                  <span v-if="loadingExcel" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  <vue-feather v-else type="search" size="16" />
                </button>
              </div>
              <div class="col-md-5">
                <label class="carga-label">Plantilla Excel:</label>
                <button type="button" class="btn btn-pill btn-plantilla" @click="descargarPlantilla">
                  Descargar Plantilla
                  <vue-feather type="download" size="16" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as XLSX from "xlsx";
import { apiFetch } from "@/services/api";
import Swal from "sweetalert2";

export default {
  name: "CargaMultipleGS1",
  data() {
    return {
      excelFile: null,
      excelFileName: "",
      excelFileError: null,
      loadingExcel: false,
      excelProgressDone: 0,
      excelProgressTotal: 0,
    };
  },
  computed: {
    maxFilasExcel() {
      return this.$store.state.configuracion.busquedaExcel.gs1.maxFilas;
    },
    porcentajeProgreso() {
      if (!this.excelProgressTotal) return 0;
      return Math.round((this.excelProgressDone / this.excelProgressTotal) * 100);
    },
  },
  created() {
    this.cargarMaxFilasExcel();
  },
  methods: {
    /** Límite de filas de "Procesar Excel con GS1" configurado en Configuración > Búsqueda Excel. */
    async cargarMaxFilasExcel() {
      try {
        await this.$store.dispatch("configuracion/consultarBusquedaExcel");
      } catch (e) {
        console.error("No se pudo consultar el límite de filas de la carga por Excel con GS1:", e);
      }
    },
    onExcelFileChange(event) {
      const [file] = event.target.files || [];
      this.excelFileError = null;
      this.excelFile = null;
      this.excelFileName = "";
      if (!file) return;
      const ext = (file.name || "").toLowerCase();
      if (!ext.endsWith(".xlsx") && !ext.endsWith(".xls")) {
        this.excelFileError = "Solo se permiten archivos .xlsx o .xls";
        event.target.value = "";
        return;
      }
      this.excelFile = file;
      this.excelFileName = file.name;
    },
    extraerColumnaCb(workbook) {
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
      if (!data.length) return { codigos: [], items: [] };
      const header = data[0].map((h) => String(h || "").trim().toLowerCase());
      const cbIdx = header.findIndex((h) => h === "cb");
      if (cbIdx === -1) return null;
      const items = [];
      for (let i = 1; i < data.length; i++) {
        const val = data[i][cbIdx];
        const str = String(val ?? "").trim().replace(/\s/g, "");
        const esValido = str && /^\d{5,14}$/.test(str);
        items.push({ cb: str || "(vacío)", esValido, codigo: esValido ? str : null });
      }
      const codigos = items.filter((x) => x.esValido).map((x) => x.codigo);
      return { codigos, items };
    },
    async procesarExcel() {
      if (!this.excelFile) return;
      this.loadingExcel = true;
      this.excelFileError = null;
      this.excelProgressDone = 0;
      this.excelProgressTotal = 0;
      const baseUrl = process.env.VUE_APP_API_BASE_URL || "http://localhost:3000";
      const CONCURRENT = 3;
      try {
        const arrayBuffer = await this.excelFile.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const allRows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
        await this.cargarMaxFilasExcel();
        const filasDatos = Math.max(allRows.length - 1, 0);
        if (filasDatos > this.maxFilasExcel) {
          this.excelFileError =
            `El archivo excede el límite de ${this.maxFilasExcel} productos (sin contar el encabezado). ` +
            `Filas detectadas: ${filasDatos}. Se rechaza el archivo.`;
          return;
        }
        const result = this.extraerColumnaCb(workbook);
        if (result === null) {
          this.excelFileError = "No se encontró la columna 'Cb' en el archivo.";
          return;
        }
        const { items } = result;
        const codigos = [...new Set(items.filter((x) => x.esValido).map((x) => x.codigo))];
        if (codigos.length === 0) {
          this.excelFileError = "No hay códigos válidos (5-14 dígitos) en la columna Cb.";
          return;
        }

        // Un solo lote para todo el Excel: el backend envía un único correo con el reporte al terminar.
        const resLote = await apiFetch(`${baseUrl}/api/imagenes/gs1/lotes`, { method: "POST" });
        if (!resLote.ok) throw new Error("No se pudo iniciar la carga en GS1.");
        const { jobId } = await resLote.json();

        this.excelProgressTotal = codigos.length;
        const reporte = [];
        try {
          for (let i = 0; i < codigos.length; i += CONCURRENT) {
            const batch = codigos.slice(i, i + CONCURRENT);
            const results = await Promise.all(
              batch.map(async (codigo) => {
                try {
                  const res = await apiFetch(`${baseUrl}/api/imagenes/gs1/lotes/${jobId}/codigos`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ GTIN: codigo }),
                  });
                  const data = res.ok ? await res.json() : {};
                  return { cb: codigo, disponible: Boolean(data.disponible) };
                } catch (e) {
                  return { cb: codigo, disponible: false };
                }
              })
            );
            reporte.push(...results);
            this.excelProgressDone = Math.min(i + batch.length, codigos.length);
          }
        } finally {
          await apiFetch(`${baseUrl}/api/imagenes/gs1/lotes/${jobId}/cerrar`, { method: "POST" }).catch((e) =>
            console.error("No se pudo cerrar el lote GS1:", e)
          );
        }
        const mapResultado = {};
        reporte.forEach((r) => {
          mapResultado[r.cb] = r.disponible;
        });
        const filasReporte = items.map((item) => {
          if (!item.esValido) {
            return { cb: item.cb, estadoGS1: "Código inválido" };
          }
          const disponible = mapResultado[item.codigo];
          return {
            cb: item.cb,
            estadoGS1: disponible ? "Disponible" : "No disponible",
          };
        });
        this.generarReporteExcelGS1(filasReporte);
        const disponibles = reporte.filter((r) => r.disponible).length;
        await Swal.fire({
          icon: "success",
          title: "Procesamiento completado",
          text:
            `${disponibles} de ${codigos.length} códigos con imagen en GS1. Se encolaron para procesamiento. ` +
            "Reporte descargado; al terminar se enviará un solo correo con el Excel de resultados.",
        });
      } catch (error) {
        console.error("Error al procesar Excel:", error);
        this.excelFileError = error.message || "No se pudo completar el procesamiento.";
      } finally {
        this.loadingExcel = false;
        this.excelProgressDone = 0;
        this.excelProgressTotal = 0;
      }
    },
    generarReporteExcelGS1(rows) {
      const headers = ["Cb", "Estado GS1"];
      const data = [
        headers,
        ...rows.map((r) => [r.cb, r.estadoGS1]),
      ];
      const ws = XLSX.utils.aoa_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Reporte GS1");
      const d = new Date();
      const fecha = `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}-${d.getFullYear()}`;
      XLSX.writeFile(wb, `Reporte GS1 ${fecha}.xlsx`);
    },
    descargarPlantilla() {
      const headers = ["cb"];
      const ws = XLSX.utils.aoa_to_sheet([headers]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Plantilla");
      XLSX.writeFile(wb, "plantilla-carga-GS1.xlsx");
    },
  },
};
</script>

<style scoped>
.carga-card {
  border: none;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.05);
}

.carga-card .card-body {
  padding: 28px 28px 56px;
}

.carga-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #3d3d3d;
}

.carga-file {
  max-width: 290px;
  height: 34px;
  padding: 0 0.75rem 0 0;
  border: 1px solid #e6e6e6;
  border-radius: 4px;
  font-size: 14px;
  color: #3d3d3d;
  line-height: 32px;
}

.carga-file::file-selector-button {
  height: 100%;
  margin: 0 12px 0 0;
  padding: 0 10px;
  border: none;
  border-right: 1px solid #e6e6e6;
  background-color: #f5f5f5;
  color: #3d3d3d;
}

.carga-file:focus {
  border-color: #5c61f2;
  box-shadow: 0 0 0 0.15rem rgba(92, 97, 242, 0.15);
}

.carga-nota {
  margin: 24px 0 0;
  font-size: 13px;
  color: #3d3d3d;
}

.carga-progreso {
  max-width: 290px;
  margin-top: 16px;
}

.carga-progreso .progress {
  height: 6px;
}

.carga-progreso .progress-bar {
  background-color: #5c61f2;
}

.btn-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 24px;
  border: none;
  border-radius: 30px;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  white-space: nowrap;
  transition: background-color 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
}

.btn-pill:hover:not(:disabled) {
  color: #fff;
  transform: translateY(-1px);
}

.btn-pill:disabled {
  color: #fff;
  opacity: 0.65;
}

.btn-plantilla {
  background-color: #65b446;
}

.btn-plantilla:hover {
  background-color: #57a03b;
  box-shadow: 0 6px 14px rgba(101, 180, 70, 0.3);
}

.btn-procesar,
.btn-procesar:disabled {
  background-color: #5c61f2;
}

.btn-procesar:hover:not(:disabled) {
  background-color: #4b50e0;
  box-shadow: 0 6px 14px rgba(92, 97, 242, 0.3);
}
</style>
